// Ported verbatim from app-web's ui-document-viewer/ui-pdf-repair.ts; keep the two in step.

// A PDF can describe its own image wrongly, and pdf.js believes the description. Ghostscript, which
// is what converts an uploaded photo into a pdf, has been seen taking a greyscale jpeg of 1099x1754
// and declaring it an image of 1099x584 in three components. Both halves of that are wrong and the
// pair is why: 1099x584x3 is the same number of bytes as 1099x1754x1, so the declaration reshapes
// the buffer instead of describing it.
//
// pdf.js sizes the pixel buffer from the declaration and fills it from the jpeg. The canvas then
// reads three bytes for every pixel where there is one, races through each row three times over, and
// runs out of image a third of the way down: the page comes out with its content tiled three times
// across a band, black beneath it. Correcting only the component count stops the tiling but leaves
// the height, so pdf.js squeezes 1754 rows of text into 584 and the page stretches them back — two
// thirds of the vertical resolution gone, every letter smeared.
//
// Chromium's own reader draws the file correctly because it trusts the jpeg's frame header over the
// pdf's declaration for both. So does this, and for the same reason: the image knows its own shape.
//
// Nothing may move while it is corrected. A pdf's cross-reference table is a list of byte offsets,
// and a file whose offsets no longer match is a file that will not open — so each rewrite is length
// preserving. The component count is one digit for another. The dimensions are written back into a
// dictionary padded to its original width, which is free: whitespace between tokens is arbitrary in
// pdf, and the space before a `/` is not needed at all.

// Byte for byte: latin-1 maps 0–255 onto the same code points, so the string is the file.
const LATIN1 = new TextDecoder('latin1');

// `/DCTDecode`, the only filter this repair reads. Every document opened is checked for it, so the
// check runs over the bytes rather than over a copy of them: a pdf carrying no jpeg — most of them —
// is answered without allocating anything.
const JPEG_FILTER = Uint8Array.from('/DCTDecode', (character) => character.charCodeAt(0));

// Every marker that opens a jpeg frame, which is the segment carrying the shape. The gaps are the
// ones that do not: 0xC4 holds Huffman tables, 0xC8 is reserved, 0xCC holds arithmetic ones.
const START_OF_FRAME = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
]);

const START_OF_SCAN = 0xda;

const IMAGE_DICTIONARY = /<<([^<>]*\/Subtype\s*\/Image[^<>]*)>>\s*stream\r?\n/g;

// A dimension may legally be a reference to an object holding the number (ISO 32000-1 §8.9.5), and
// `/Height 5 0 R` overwritten in place reads as `/Height 1754 0 R` — a different object entirely.
// The `\b` is what keeps the digits whole: without it the match gives back its last digit to satisfy
// the lookahead, and rewrites half the number. Ghostscript writes them direct, so this only declines
// files from elsewhere.
const DIRECT_WIDTH = /(\/Width\s+)\d+\b(?!\s+0\s+R)/;
const DIRECT_HEIGHT = /(\/Height\s+)\d+\b(?!\s+0\s+R)/;

interface JpegFrame {
  width: number;
  height: number;
  components: number;
}

/** Whether the count could be read at all, told apart from a count that was already right. */
type ComponentCount = 'repaired' | 'agreed' | 'unreachable';

const contains = (haystack: Uint8Array, needle: Uint8Array): boolean => {
  outer: for (let at = 0; at <= haystack.length - needle.length; at++) {
    for (let index = 0; index < needle.length; index++) {
      if (haystack[at + index] !== needle[index]) {
        continue outer;
      }
    }

    return true;
  }

  return false;
};

// Everything read from the frame is written back into the file, so a value no jpeg could carry is a
// value the marker walk misread — the walk desynchronises whenever a segment length is wrong, which
// is the very population this repair is for. A count outside 1–4 is the worst of them: it is written
// as `0x30 + count`, so 200 spells `/N ø` and the colour space stops being a pdf number at all.
const isPlausible = ({ width, height, components }: JpegFrame): boolean =>
  width > 0 && height > 0 && components >= 1 && components <= 4;

/** What the jpeg says it is, read from its own start-of-frame marker. */
const jpegFrame = (bytes: Uint8Array, from: number, length: number): JpegFrame | undefined => {
  const end = Math.min(from + length, bytes.length);

  if (bytes[from] !== 0xff || bytes[from + 1] !== 0xd8) {
    return undefined;
  }

  let at = from + 2;

  while (at < end - 1) {
    if (bytes[at] !== 0xff) {
      at += 1;
      continue;
    }

    const marker = bytes[at + 1];

    if (START_OF_FRAME.has(marker)) {
      // A frame header cut short by a truncated stream would read past it and answer with numbers
      // from whatever follows, and those numbers are written back into the file.
      if (at + 9 >= end) {
        return undefined;
      }

      const frame = {
        height: (bytes[at + 5] << 8) | bytes[at + 6],
        width: (bytes[at + 7] << 8) | bytes[at + 8],
        components: bytes[at + 9],
      };

      return isPlausible(frame) ? frame : undefined;
    }

    // The scan is the image data itself: the frame header is behind us and there is no more to learn.
    if (marker === START_OF_SCAN) {
      return undefined;
    }

    // Padding and the standalone markers carry no length to skip by.
    if (marker === 0xff || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd9)) {
      at += 2;
      continue;
    }

    at += 2 + ((bytes[at + 2] << 8) | bytes[at + 3]);
  }

  return undefined;
};

/** Where each `<n> 0 obj` begins, so that following a reference is a lookup rather than a search. */
const indexObjects = (file: string): Map<string, number> => {
  const objects = new Map<string, number>();

  for (const match of file.matchAll(/(?:^|[^0-9])(\d+)\s+0\s+obj/g)) {
    objects.set(match[1], match.index + match[0].indexOf(match[1]));
  }

  return objects;
};

/** As much of an object as any dictionary this reads could occupy. */
const bodyOf = (
  file: string,
  objects: Map<string, number>,
  number: string,
): { start: number; body: string } | undefined => {
  const start = objects.get(number);

  return start === undefined ? undefined : { start, body: file.slice(start, start + 600) };
};

const write = (bytes: Uint8Array, at: number, text: string): void => {
  for (let index = 0; index < text.length; index++) {
    bytes[at + index] = text.charCodeAt(index);
  }
};

/** The same tokens spelled to an exact width, or nothing when the slack to do it isn't there. */
const toWidth = (text: string, width: number): string | undefined => {
  if (text.length <= width) {
    return text + ' '.repeat(width - text.length);
  }

  // Whitespace is only spare where it separates tokens. Inside a string it is content, and before a
  // comment it is what ends the line — neither belongs in an image dictionary, so meeting one is
  // reason enough to leave the file alone.
  if (/[(%]/.test(text)) {
    return undefined;
  }

  let shortened = text;

  while (shortened.length > width) {
    // A `/` opens a name, so it is its own separator: the whitespace in front of it is spare.
    const spared = shortened.replace(/\s(?=\/)/, '');

    if (spared === shortened) {
      return undefined;
    }

    shortened = spared;
  }

  return shortened;
};

/** Corrects the size an image is declared to be to the size its jpeg actually is. */
const repairDimensions = (
  bytes: Uint8Array,
  at: number,
  dictionary: string,
  frame: JpegFrame,
): boolean => {
  if (!DIRECT_WIDTH.test(dictionary) || !DIRECT_HEIGHT.test(dictionary)) {
    return false;
  }

  const sized = dictionary
    .replace(DIRECT_WIDTH, `$1${frame.width}`)
    .replace(DIRECT_HEIGHT, `$1${frame.height}`);

  if (sized === dictionary) {
    return false;
  }

  const padded = toWidth(sized, dictionary.length);

  if (!padded) {
    return false;
  }

  write(bytes, at, padded);

  return true;
};

/** Corrects the component count of the ICC colour space an image points at to the jpeg's own. */
const repairComponentCount = (
  bytes: Uint8Array,
  file: string,
  objects: Map<string, number>,
  dictionary: string,
  components: number,
): ComponentCount => {
  const colourSpace = /\/ColorSpace\s*(?:\[\s*\/ICCBased\s*(\d+)\s+0\s+R\s*\]|(\d+)\s+0\s+R)/.exec(
    dictionary,
  );
  const reference = colourSpace?.[1] ?? colourSpace?.[2];
  const referenced = reference && bodyOf(file, objects, reference);

  if (!referenced) {
    return 'unreachable';
  }

  // The reference is either the ICC stream or the array naming it, so follow at most one hop.
  const hop = /\[\s*\/ICCBased\s+(\d+)\s+0\s+R/.exec(referenced.body)?.[1];
  const icc = hop ? bodyOf(file, objects, hop) : referenced;
  const declared = icc && /\/N\s+(\d)/.exec(icc.body);

  if (!declared) {
    return 'unreachable';
  }

  if (+declared[1] === components) {
    return 'agreed';
  }

  // One digit for another, so the profile itself keeps saying three. pdf.js reads `/N`, and a wrong
  // profile on a page that draws beats a right one on a page that does not.
  bytes[icc.start + declared.index + declared[0].indexOf(declared[1])] = 0x30 + components;

  return 'repaired';
};

/**
 * Corrects a pdf whose images contradict themselves, in place, and reports whether it had to.
 * Anything it does not recognise it leaves alone: the cost of a wrong repair is a document nobody
 * can read, so only a declaration the jpeg flatly contradicts is touched.
 */
export const repairContradictedImages = (bytes: Uint8Array): boolean => {
  if (!contains(bytes, JPEG_FILTER)) {
    return false;
  }

  const file = LATIN1.decode(bytes);
  const objects = indexObjects(file);
  let repaired = false;

  for (const image of file.matchAll(IMAGE_DICTIONARY)) {
    const dictionary = image[1];
    const length = /\/Length\s+(\d+)/.exec(dictionary);

    if (!length || !/\/Filter\s*\/DCTDecode/.test(dictionary)) {
      continue;
    }

    const frame = jpegFrame(bytes, image.index + image[0].length, +length[1]);

    if (!frame) {
      continue;
    }

    const counted = repairComponentCount(bytes, file, objects, dictionary, frame.components);

    // What tiles the page is the component count, so a height corrected without it leaves the symptom
    // in place on a file that now reads as repaired — a named colour space, resolved through the
    // page's resources, is the one this reaches through neither.
    if (counted === 'unreachable') {
      continue;
    }

    repaired = counted === 'repaired' || repaired;
    repaired = repairDimensions(bytes, image.index + '<<'.length, dictionary, frame) || repaired;
  }

  return repaired;
};
